import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiCheck } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';

import { ChatComposerRow } from '#components/chat-composer-row';
import { toaster } from '#components/ui/toaster';
import {
  OAuthProvider,
  type MyOAuthConnectionsQuery,
} from '#graphql/generated';
import { DELETE_OAUTH_CONNECTION } from '#graphql/mutations/delete-oauth-connection';
import { TEST_GMAIL_CONNECTION } from '#graphql/mutations/test-gmail-connection';
import { UPDATE_OAUTH_CONNECTION } from '#graphql/mutations/update-oauth-connection';
import { MY_OAUTH_CONNECTIONS } from '#graphql/queries/my-oauth-connections';
import { useAppConfig } from '#providers/app-config-provider';

import {
  GoogleOAuthConnectModal,
  type GoogleCapabilitySelection,
} from './settings-integrations-section/modals/google-oauth-connect-modal';

type ConnectionRow = MyOAuthConnectionsQuery['myOAuthConnections'][number];
type GoogleCapabilityGrant = 'gmailReadonly' | 'calendarReadonly';
type GoogleConnectionMetadata = {
  email?: string | null;
  name?: string | null;
  googleCapabilities?: GoogleCapabilityGrant[] | null;
};
type TrelloConnectionMetadata = {
  fullName?: string | null;
  username?: string | null;
};

const GMAIL_SCOPE = 'https://www.googleapis.com/auth/gmail.readonly';
const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';

function oauthErrorMessage(code: string): string {
  const map: Record<string, string> = {
    google_denied: 'Google sign-in was cancelled.',
    google_not_configured: 'Google linking is not configured on the server.',
    google_provider_error: 'Google returned an error during sign-in.',
    google_invalid_request: 'The OAuth request was incomplete.',
    google_missing_refresh:
      'Google did not return a refresh token. Try revoking app access in your Google account and connect again.',
    google_invalid_profile: 'Could not read your Google profile.',
    google_callback_failed: 'Could not finish linking Google. Try again.',
    google_invalid_state: 'The sign-in session expired. Try connecting again.',
    google_reconnect_conflict:
      'That Google account is already linked on another connection. Remove or reconnect that one first, or pick a different Google account.',
    gmail_denied: 'Google sign-in was cancelled.',
    gmail_provider_error: 'Google returned an error during sign-in.',
    gmail_invalid_request: 'The OAuth request was incomplete.',
    gmail_missing_refresh:
      'Google did not return a refresh token. Try revoking app access in your Google account and connect again.',
    gmail_invalid_profile: 'Could not read your Google profile.',
    gmail_callback_failed: 'Could not finish linking Google. Try again.',
    gmail_invalid_state: 'The sign-in session expired. Try connecting again.',
    gmail_reconnect_conflict:
      'That Google account is already linked on another connection. Remove or reconnect that one first, or pick a different Google account.',
    trello_denied: 'Trello access was cancelled.',
    trello_not_configured: 'Trello linking is not configured on the server.',
    trello_invalid_request: 'The Trello OAuth request was incomplete.',
    trello_invalid_state:
      'The Trello sign-in session expired. Try connecting again.',
    trello_invalid_profile: 'Could not read your Trello profile.',
    trello_callback_failed: 'Could not finish linking Trello. Try again.',
  };
  return map[code] ?? 'Something went wrong while connecting.';
}

function getGrantedCapabilities(
  connection: ConnectionRow,
): GoogleCapabilityGrant[] {
  if (connection.provider !== OAuthProvider.Google) {
    return [];
  }

  const metadata = connection.metadata as GoogleConnectionMetadata | null;
  const fromMetadata = Array.isArray(metadata?.googleCapabilities)
    ? metadata.googleCapabilities.filter(
        (value): value is GoogleCapabilityGrant =>
          value === 'gmailReadonly' || value === 'calendarReadonly',
      )
    : [];
  if (fromMetadata.length > 0) {
    return fromMetadata;
  }

  const scopes = connection.scopes.split(/\s+/);
  const granted: GoogleCapabilityGrant[] = [];
  if (scopes.includes(GMAIL_SCOPE)) granted.push('gmailReadonly');

  if (scopes.includes(CALENDAR_SCOPE)) granted.push('calendarReadonly');

  return granted;
}

function toSelection(
  connection?: ConnectionRow | null,
): GoogleCapabilitySelection {
  if (!connection || connection.provider !== OAuthProvider.Google) {
    return { gmail: true, calendar: true };
  }

  const granted = getGrantedCapabilities(connection);
  return {
    gmail: granted.includes('gmailReadonly'),
    calendar: granted.includes('calendarReadonly'),
  };
}

function capabilitySelectionParam(
  selection: GoogleCapabilitySelection,
): string {
  const out: string[] = [];
  if (selection.gmail) out.push('gmail');

  if (selection.calendar) out.push('calendar');

  return out.join(',');
}

function capabilityLabels(connection: ConnectionRow): string[] {
  if (connection.provider !== OAuthProvider.Google) {
    return [];
  }

  const granted = getGrantedCapabilities(connection);
  const out: string[] = [];
  if (granted.includes('gmailReadonly')) out.push('Email Contents');

  if (granted.includes('calendarReadonly')) out.push('Calendar');

  return out;
}

function connectionHint(connection: ConnectionRow): string | null {
  if (connection.provider === OAuthProvider.Google) {
    const metadata = connection.metadata as GoogleConnectionMetadata | null;
    return typeof metadata?.email === 'string' && metadata.email.length > 0
      ? metadata.email
      : null;
  }

  if (connection.provider === OAuthProvider.Trello) {
    const metadata = connection.metadata as TrelloConnectionMetadata | null;
    if (
      typeof metadata?.username === 'string' &&
      metadata.username.length > 0
    ) {
      return `@${metadata.username}`;
    }

    return typeof metadata?.fullName === 'string' &&
      metadata.fullName.length > 0
      ? metadata.fullName
      : null;
  }

  return null;
}

function providerLabel(connection: ConnectionRow): string {
  return connection.provider === OAuthProvider.Trello ? 'Trello' : 'Google';
}

function OAuthConnectionCard({
  connection,
  onRemoved,
  onReconnectGoogle,
}: {
  connection: ConnectionRow;
  onRemoved: () => void;
  onReconnectGoogle: (connection: ConnectionRow) => void;
}) {
  const [localDescription, setLocalDescription] = useState(
    connection.description,
  );
  useEffect(() => {
    setLocalDescription(connection.description);
  }, [connection.description, connection.id]);

  const [updateOAuthConnection, { loading: saving }] = useMutation(
    UPDATE_OAUTH_CONNECTION,
  );
  const [deleteOAuthConnection, { loading: deleting }] = useMutation(
    DELETE_OAUTH_CONNECTION,
  );
  const [testGmailConnection, { loading: testing }] = useMutation(
    TEST_GMAIL_CONNECTION,
  );

  const trimmedLocal = localDescription.trim();
  const trimmedServer = connection.description.trim();
  const saveDisabled = trimmedLocal === trimmedServer;
  const hint = connectionHint(connection);
  const isGoogle = connection.provider === OAuthProvider.Google;
  const labels = capabilityLabels(connection);
  const supportsGmail = labels.includes('Email Contents');
  const label = providerLabel(connection);

  const save = async () => {
    try {
      await updateOAuthConnection({
        variables: {
          input: { id: connection.id, description: trimmedLocal },
        },
      });
      toaster.create({
        title: 'Saved',
        description: 'Connection description updated.',
        type: 'success',
        duration: 2500,
        closable: true,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not save description.',
        type: 'error',
      });
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        `Remove this ${label} connection? You can connect again later.`,
      )
    ) {
      return;
    }

    try {
      const res = await deleteOAuthConnection({
        variables: { id: connection.id },
      });
      if (!res.data?.deleteOAuthConnection) {
        toaster.create({
          title: 'Not removed',
          description: 'Connection was not found or already removed.',
          type: 'warning',
        });
        return;
      }

      toaster.create({
        title: 'Removed',
        description: `${label} connection removed.`,
        type: 'success',
        duration: 2500,
        closable: true,
      });
      onRemoved();
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not remove connection.',
        type: 'error',
      });
    }
  };

  const testConnection = async () => {
    try {
      const res = await testGmailConnection({
        variables: { connectionId: connection.id },
      });
      const payload = res.data?.testGmailConnection;
      if (!payload) {
        toaster.create({
          title: 'Error',
          description: 'No response from server.',
          type: 'error',
        });
        return;
      }

      if (payload.success) {
        const preview =
          payload.subjects.length > 0
            ? payload.subjects.slice(0, 3).join(' · ')
            : 'Inbox sample is empty (no recent messages matched).';
        toaster.create({
          title: 'Gmail test OK',
          description: `${payload.fetchedCount} message(s): ${preview}${payload.subjects.length > 3 ? ' …' : ''}`,
          type: 'success',
          duration: 5000,
          closable: true,
        });
        return;
      }

      toaster.create({
        title: 'Gmail test failed',
        description: payload.errorMessage ?? 'Unknown error.',
        type: 'error',
        duration: 6000,
        closable: true,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not reach Gmail.',
        type: 'error',
      });
    }
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="border"
      borderRadius="md"
      p={4}
      bg="bg.subtle"
    >
      <VStack gap={3} align="stretch">
        <HStack justify="space-between" align="flex-start" flexWrap="wrap">
          <VStack align="start" gap={1}>
            <HStack gap={2} flexWrap="wrap">
              <Badge colorPalette="blue" size="sm">
                {label}
              </Badge>
              {hint ? (
                <Text fontSize="xs" color="fg.muted">
                  {hint}
                </Text>
              ) : null}
            </HStack>
            <Text fontSize="xs" color="fg.muted">
              Account id: {connection.providerAccountId}
            </Text>
            {isGoogle ? (
              <HStack gap={2} flexWrap="wrap">
                {labels.map((capability) => (
                  <Badge key={capability} size="sm" variant="subtle">
                    {capability}
                  </Badge>
                ))}
                {labels.length === 0 ? (
                  <Text fontSize="xs" color="fg.muted">
                    No granted capabilities recorded.
                  </Text>
                ) : null}
              </HStack>
            ) : null}
          </VStack>
          <HStack gap={2}>
            {supportsGmail ? (
              <Button
                size="sm"
                variant="outline"
                loading={testing}
                disabled={deleting}
                onClick={() => void testConnection()}
              >
                Test Gmail
              </Button>
            ) : null}
            {isGoogle ? (
              <Button
                size="sm"
                variant="outline"
                disabled={deleting || testing}
                onClick={() => onReconnectGoogle(connection)}
              >
                Reconnect
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              colorPalette="red"
              loading={deleting}
              disabled={testing}
              onClick={() => void remove()}
            >
              Remove
            </Button>
          </HStack>
        </HStack>
        {isGoogle ? (
          <Text fontSize="xs" color="fg.muted">
            Reconnect keeps your label and connection id; you can use the same
            Google account or sign in with a different one.
          </Text>
        ) : (
          <Text fontSize="xs" color="fg.muted">
            Trello is linked with read-only access for this account.
          </Text>
        )}
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            Description (for you and future AI features)
          </Text>
          <ChatComposerRow
            value={localDescription}
            onChange={setLocalDescription}
            onSubmit={() => void save()}
            placeholder="Label this connection…"
            submitOnEnter={false}
            autoGrow
            disabled={saving}
            loading={saving}
            submitDisabled={saveDisabled}
            submitAriaLabel="Save description"
            submitTitle="Save description"
            submitIcon={<FiCheck size={18} strokeWidth={2.5} aria-hidden />}
          />
        </Box>
      </VStack>
    </Box>
  );
}

export function SettingsIntegrationsSection() {
  const { config } = useAppConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error, refetch } = useQuery(MY_OAUTH_CONNECTIONS);
  const [connectOpen, setConnectOpen] = useState(false);
  const [reconnectTarget, setReconnectTarget] = useState<ConnectionRow | null>(
    null,
  );

  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const oauthError = searchParams.get('oauth_error');
    if (!oauth && !oauthError) return;

    const next = new URLSearchParams(searchParams);
    next.delete('oauth');
    next.delete('oauth_error');
    setSearchParams(next, { replace: true });

    if (oauth === 'google_success' || oauth === 'gmail_success') {
      void refetch();
      toaster.create({
        title: 'Google connected',
        description: 'You can add a description below to label this account.',
        type: 'success',
        duration: 4000,
        closable: true,
      });
      return;
    }

    if (
      oauth === 'google_reconnect_success' ||
      oauth === 'gmail_reconnect_success'
    ) {
      void refetch();
      toaster.create({
        title: 'Google reconnected',
        description:
          'Tokens and granted capabilities updated; your description is unchanged.',
        type: 'success',
        duration: 4000,
        closable: true,
      });
      return;
    }

    if (oauth === 'trello_success') {
      void refetch();
      toaster.create({
        title: 'Trello connected',
        description: 'You can add a description below to label this account.',
        type: 'success',
        duration: 4000,
        closable: true,
      });
      return;
    }

    if (oauthError) {
      toaster.create({
        title: 'Could not connect integration',
        description: oauthErrorMessage(oauthError),
        type: 'error',
        duration: 6000,
        closable: true,
      });
    }
  }, [searchParams, setSearchParams, refetch]);

  const rows = data?.myOAuthConnections ?? [];
  const googleAuthorizeUrl = `${config.apiHost}/api/oauth/google/authorize`;
  const trelloAuthorizeUrl = `${config.apiHost}/api/oauth/trello/authorize`;

  const startGoogleOAuth = (
    selection: GoogleCapabilitySelection,
    reconnectConnectionId?: string,
  ) => {
    const url = new URL(googleAuthorizeUrl);
    url.searchParams.set('capabilities', capabilitySelectionParam(selection));
    if (reconnectConnectionId) {
      url.searchParams.set('reconnect', reconnectConnectionId);
    }

    window.location.href = url.toString();
  };

  return (
    <Box>
      <Text fontSize="sm" fontWeight="medium" color="fg" mb={2}>
        Integrations
      </Text>
      <Text fontSize="xs" color="fg.muted" mb={3}>
        Connect external accounts. Labels help you tell connections apart.
      </Text>

      <HStack gap={2} flexWrap="wrap" mb={4}>
        <Button
          size="sm"
          colorPalette="blue"
          onClick={() => setConnectOpen(true)}
        >
          Connect Google
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            window.location.href = trelloAuthorizeUrl;
          }}
        >
          Connect Trello
        </Button>
      </HStack>

      {loading && !data ? (
        <Text fontSize="sm" color="fg.muted">
          Loading connections…
        </Text>
      ) : null}
      {error ? (
        <Text fontSize="sm" color="red.500">
          {error.message}
        </Text>
      ) : null}

      {!loading && rows.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          No accounts linked yet. Use Connect Google or Connect Trello above to
          add one.
        </Text>
      ) : null}

      {rows.length > 0 ? (
        <VStack gap={3} align="stretch">
          {rows.map((connection) => (
            <OAuthConnectionCard
              key={connection.id}
              connection={connection}
              onRemoved={() => void refetch()}
              onReconnectGoogle={(target) => setReconnectTarget(target)}
            />
          ))}
        </VStack>
      ) : null}

      <GoogleOAuthConnectModal
        open={connectOpen}
        onOpenChange={setConnectOpen}
        initialSelection={toSelection()}
        mode="connect"
        onConfirm={(selection) => {
          setConnectOpen(false);
          startGoogleOAuth(selection);
        }}
      />
      <GoogleOAuthConnectModal
        open={reconnectTarget != null}
        onOpenChange={(open) => {
          if (!open) setReconnectTarget(null);
        }}
        initialSelection={toSelection(reconnectTarget)}
        mode="reconnect"
        onConfirm={(selection) => {
          const reconnectId = reconnectTarget?.id;
          setReconnectTarget(null);
          if (reconnectId) {
            startGoogleOAuth(selection, reconnectId);
          }
        }}
      />
    </Box>
  );
}
